// RenameDocumentModal — used in two places:
//   1. Pre-upload: review/adjust the filename before the file lands
//      in SharePoint. (DocumentsTab intercepts file selection and
//      pops this so the user can rename at the natural moment.)
//   2. Post-upload: rename an existing document via the pencil icon
//      on the DocumentsTab list row.
//
// v1.x phase 11.16, Tier A items 1+2.
//
// Both flows share the same modal because the affordance is
// identical from the user's perspective: "what should this file be
// called?" The caller decides whether to upload or rename via the
// onSubmit callback.

import { useEffect, useRef, useState } from "react";

import { Button } from "../ui";
import { Modal } from "./Modal";

export interface RenameDocumentModalProps {
  open: boolean;
  // Current filename — pre-fills the input. For pre-upload this is
  // the original file.name; for post-upload it's the documents.filename.
  initialFilename: string;
  // Optional title override. Defaults to "Rename file".
  title?: string;
  // Called with the (sanitized server-side) new filename. Returns a
  // promise that resolves on success; modal stays open until then so
  // the caller can show a spinner.
  onSubmit: (newFilename: string) => Promise<void>;
  onCancel: () => void;
  // Optional submit-button label override ("Upload" vs "Save").
  submitLabel?: string;
}

export function RenameDocumentModal({
  open,
  initialFilename,
  title,
  onSubmit,
  onCancel,
  submitLabel,
}: RenameDocumentModalProps) {
  const [name, setName] = useState(initialFilename);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset state on open + select-all the basename so user can type
  // immediately without first nuking the existing text.
  useEffect(() => {
    if (!open) return;
    setName(initialFilename);
    setError(null);
    setSubmitting(false);
    setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      // Select up to the last dot so the extension stays as a hint
      // but the basename is selected ready for typing.
      const lastDot = initialFilename.lastIndexOf(".");
      const end =
        lastDot > 0 && lastDot < initialFilename.length
          ? lastDot
          : initialFilename.length;
      el.setSelectionRange(0, end);
    }, 50);
  }, [open, initialFilename]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Filename is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(trimmed);
      // Caller closes on success.
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Couldn't save filename.";
      setError(msg);
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!submitting) onCancel();
      }}
      title={title ?? "Rename file"}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={submitting}
            type="button"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={(e) => {
              e.preventDefault();
              void handleSubmit(e as unknown as React.FormEvent);
            }}
            disabled={submitting || name.trim().length === 0}
            type="button"
          >
            {submitting ? "Saving…" : (submitLabel ?? "Save")}
          </Button>
        </>
      }
    >
      <form
        onSubmit={handleSubmit}
        className="rename-doc-modal-form"
      >
        {error ? (
          <div
            className="rename-doc-modal-error"
            role="alert"
          >
            {error}
          </div>
        ) : null}
        <label className="rename-doc-modal-label" htmlFor="rename-doc-input">
          FILENAME
        </label>
        <input
          ref={inputRef}
          id="rename-doc-input"
          type="text"
          className="rename-doc-modal-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
          disabled={submitting}
          required
        />
        <p className="rename-doc-modal-hint">
          Just type what you want the file called — no need to add{" "}
          <code>.pdf</code> or other file extensions, and don't worry about
          the household or record name. We add those automatically so the
          file is easy to find later.
        </p>
      </form>
    </Modal>
  );
}
